import React from 'react';
import { styled, StyledProps } from '@glitz/react';
import { H1, ResponsiveSize, HeadingLabel } from 'Shared/SharedComponents/atoms/Typography';
import { ButtonHeight, ButtonTheme, SolidButton } from 'Shared/TextButtons';
import Breadcrumbs from 'Shared/SharedComponents/atoms/Breadcrumbs';
import BreadcrumbsViewModel from 'Shared/Breadcrumbs/BreadcrumbsViewModel.type';
import { createGridClasses, createOffsetGridClassLarge } from 'Shared/grid';
import { GridWrap } from 'Shared/PageLayout';
import * as style from 'Shared/Style';

type TopContentPropType = React.HTMLAttributes<HTMLElement> & {
  label?: string;
  heading: string;
  headingSize?: ResponsiveSize;
  intro: string;
  isCompact: boolean;
  modalButtonText?: string;
  modalButtonIconAfter?: React.ReactNode;
  modalButtonClick?: () => void;
  handleReadMore?: Function | boolean;
  readMoreLabel?: String
};

type TopContentWidePropType = TopContentPropType & {
  breadcrumbs?: BreadcrumbsViewModel[];
};

type BreadcrumbWrapperPropType = React.HTMLAttributes<HTMLElement> & {
  isCompact: boolean;
  breadcrumbs?: BreadcrumbsViewModel[];
  isProductPage?: boolean;
};

const TopLabel = styled(HeadingLabel, {
  margin: {
    top: 0,
    bottom: '16px',
  },
});

const ReadMoreWrap = styled.div({
  display: 'flex',
  marginTop: '8px',
});

const ReadMore = styled.span({
  color: style.colors.monochrome.a11ygrey,
  cursor: 'pointer',
  textTransform: 'uppercase',
  fontSize: '14px',
  fontWeight: 500,
});

const TopContentHeading = styled(H1, {
  wordBreak: 'break-word'
});

export const BreadcrumbWrapper = styled((props: BreadcrumbWrapperPropType & StyledProps) => {
  const { isCompact, breadcrumbs, children, isProductPage = false, compose } = props;

  return (
    <GridWrap className="grid"
      css={compose({
        rowGap: '12px',
        paddingTop: '12px',
        ...(!isCompact && {
          rowGap: '24px',
          ...(!isProductPage && {
            paddingTop: '48px'
          })
        })
      })}
    >
      <styled.Div className={createGridClasses(12, 1)}
        css={{
          ...(isCompact && {
            marginLeft: '-12px',
            marginRight: '-12px',
          }),
          ...(!isCompact && isProductPage && {
            paddingTop: '48px'
          })
        }}
      >
        {breadcrumbs && <Breadcrumbs vertical={!isCompact} breadcrumbs={breadcrumbs} isCompact={isCompact} isProductPage={isProductPage} />}
      </styled.Div>
      {children}
    </GridWrap>
  );
});

const TopContentWideWrapper = styled(BreadcrumbWrapper, {
  padding: { bottom: '24px' },
  ['@media ' + style.mediaMinLarge]: {
    padding: { bottom: '48px' }
  },
})

export const TopContentWide = (props: TopContentWidePropType) => {
  const { isCompact, breadcrumbs, children } = props;
  return (
    <TopContentWideWrapper
      isCompact={isCompact}
      breadcrumbs={breadcrumbs}
    >
      <TopContent {...props} isCompact={isCompact}>
        {children}
      </TopContent>
    </TopContentWideWrapper>
  );
};

const TopContentButton = styled(SolidButton, {
  display: 'inline-flex',
  marginTop: '12px',
  ['@media ' + style.mediaMinLarge]: {
    marginTop: '24px',
  },
})

const TopContentIntro = styled.p({
  margin: { xy: 0 },
  padding: { xy: 0 }
})

const TopContentWrap = styled.div({
  ['@media ' + style.mediaUpToLarge]: {
    marginTop: '16px',
  },
})

const TopContentChildren = styled.div({
  marginTop: '24px',
  ['@media ' + style.mediaMinLarge]: {
    marginTop: '48px',
  },
})

export const TopContent = (props: TopContentPropType) => {
  const {
    isCompact,
    label,
    heading,
    headingSize,
    modalButtonText,
    modalButtonClick,
    modalButtonIconAfter,
    intro,
    children,
    handleReadMore = false,
    readMoreLabel = ''
  } = props;
  return (
    <div className={createGridClasses(12, 11, createOffsetGridClassLarge(2) + (isCompact ? '' : ' grid subgrid-11 row__content-wrap'))}>
      {label && <TopLabel className={createGridClasses(12, 11)}>{label}</TopLabel>}
      <div className={createGridClasses(12, 6)}>
        <div>
          <TopContentHeading fontSize={headingSize}>{heading}</TopContentHeading>
          {modalButtonText && (
            <TopContentButton
              height={ButtonHeight.Small}
              padding={{ x: 12 }}
              text={modalButtonText}
              onClick={() => modalButtonClick()}
              iconAfter={modalButtonIconAfter && modalButtonIconAfter}
              theme={ButtonTheme.WhiteToBlack}
            />
          )}
        </div>
      </div>
      <TopContentWrap className={createGridClasses(12, 3, createOffsetGridClassLarge(8))}>
        <TopContentIntro>{intro}</TopContentIntro>
        {handleReadMore &&
          <ReadMoreWrap>
            <ReadMore onClick={() => {
              if (typeof handleReadMore !== 'function') {
                return
              }
              handleReadMore()
            }}>
              {readMoreLabel}
            </ReadMore>
          </ReadMoreWrap>
        }
      </TopContentWrap>
      <TopContentChildren className={createGridClasses(12, 11)}>
        {children}
      </TopContentChildren>
    </div>
  );
};
